import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../lib/api';
import { toast } from 'sonner';
import { Ban, Loader2 } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Checkbox } from './ui/checkbox';
import { PassReasonPicker, passReasonText } from './PassReasonPicker';

function PassDialog({ target, onClose, onPassed }) {
  const [text, setText] = useState('');
  const [tags, setTags] = useState([]);
  const [bench, setBench] = useState(false);
  const [busy, setBusy] = useState(false);
  useEffect(() => { setText(''); setTags([]); setBench(false); }, [target]);
  const submit = async () => {
    const reason = [passReasonText(tags), text.trim()].filter(Boolean).join('; ');
    if (!reason) { toast.error('Bitte Grund wählen oder eintippen'); return; }
    setBusy(true);
    try {
      const res = await api.turnDownProject(target.id, reason, bench, 'explorer', tags);
      if (res.data.status === 'passed') { onPassed(target.id, bench); toast.success(`${target.name} passed`); }
      else toast.success(res.data.message || 'Pass vorgeschlagen — wartet auf Bestätigung');
      onClose();
    } catch (err) { toast.error(err.response?.data?.detail || 'Pass fehlgeschlagen'); }
    setBusy(false);
  };
  return (
    <Dialog open={!!target} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="bg-card border-border rounded-sm max-w-lg" data-testid="infra-pass-dialog" aria-describedby={undefined}>
        <DialogHeader>
          <DialogTitle className="font-heading text-lg text-foreground flex items-center gap-2">
            <Ban className="w-5 h-5 text-red-400" /> Pass — {target?.name}
          </DialogTitle>
        </DialogHeader>
        <PassReasonPicker selected={tags} onToggle={(id) => setTags(prev => prev.includes(id) ? prev.filter(t => t !== id) : [...prev, id])} />
        <textarea value={text} onChange={e => setText(e.target.value)} rows={2} maxLength={300} placeholder="Optionaler Zusatz zum Grund…"
          data-testid="infra-pass-reason-input"
          className="w-full bg-secondary border border-border rounded-sm p-2 text-xs text-foreground resize-none focus:outline-none focus:border-red-500/50" />
        <label className="flex items-center gap-2 text-xs text-foreground cursor-pointer">
          <Checkbox checked={bench} onCheckedChange={v => setBench(!!v)} /> Benchen — bleibt im Explorer auffindbar
        </label>
        <div className="flex justify-end gap-2">
          <Button variant="ghost" onClick={onClose} className="text-xs h-8 px-3">Abbrechen</Button>
          <Button disabled={busy || (!tags.length && !text.trim())} onClick={submit} data-testid="infra-pass-confirm-btn"
            className="bg-red-700 hover:bg-red-600 text-white rounded-sm font-medium text-xs h-8 px-3">
            {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : 'Pass'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// Shared row actions (open / flag / bookmark / pass) for CompanyRow lists outside the KI Deal Screening page
export function useRowActions(from) {
  const navigate = useNavigate();
  const [viewedSet, setViewedSet] = useState(() => new Set());
  const [bmSet, setBmSet] = useState(() => new Set());
  const [flags, setFlags] = useState({});
  const [passed, setPassed] = useState({});
  const [passTarget, setPassTarget] = useState(null);

  useEffect(() => {
    api.getViewedDeals().then(r => setViewedSet(new Set(r.data?.project_ids || []))).catch(() => {});
    api.getBookmarks().then(r => setBmSet(new Set(r.data?.project_ids || []))).catch(() => {});
  }, []);

  const openDeal = useCallback((id, extra) => {
    setViewedSet(prev => new Set(prev).add(id));
    navigate(`/projects/${id}`, { state: { from, ...(extra || {}) } });
  }, [navigate, from]);

  const toggleFlag = async (e, r) => {
    e.stopPropagation();
    const cur = flags[r.id] ?? r.ac_flagged;
    try {
      await api.setAcFlag(r.id, !cur);
      setFlags(prev => ({ ...prev, [r.id]: !cur }));
      toast.success(!cur ? `${r.name} gemerkt (!)` : 'Markierung entfernt');
    } catch { toast.error('Markieren fehlgeschlagen'); }
  };

  const toggleBookmark = async (e, r) => {
    e.stopPropagation();
    try {
      const res = await api.toggleBookmark(r.id);
      setBmSet(new Set(res.data?.project_ids || []));
      toast.success(res.data?.action === 'added' ? `${r.name} auf deine Merkliste gesetzt` : 'Von der Merkliste entfernt');
    } catch { toast.error('Bookmarken fehlgeschlagen'); }
  };

  const decorate = (rows) => (rows || []).map(r => ({
    ...r,
    ac_flagged: flags[r.id] ?? r.ac_flagged,
    pipeline_stage: passed[r.id] ? 'passed' : r.pipeline_stage,
  }));

  const rowProps = { viewedSet, onOpen: openDeal, onFlag: toggleFlag, bookmarkedSet: bmSet, onBookmark: toggleBookmark, onPassClick: setPassTarget };
  const passDialog = <PassDialog target={passTarget} onClose={() => setPassTarget(null)} onPassed={(id) => setPassed(prev => ({ ...prev, [id]: true }))} />;
  return { rowProps, decorate, passDialog };
}
